import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useVorgaenge } from '@/adapter/opm-cockpit-backend/useVorgangApi';
import { Skeleton } from '@/components/ui/skeleton';
import { VORGANG_PROZESS_SCHRITT_LABEL } from '@/constants/vorgangProzessSchritt';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import { defaultFilters } from '@/hooks/useVorgangFilters';
import { VorgangTable } from '@/pages/Vorgaenge/VorgangTable';
import type { VorgangBearbeitungLocationState } from '@/pages/VorgangBearbeitung';
import type { VorgangListeItem } from '@/types';
import { AbarbeitungsSchrittAuswahl } from './components/AbarbeitungsSchrittAuswahl';
import { PfadWertBearbeitung } from './components/PfadWertBearbeitung';
import { useMassenverarbeitungSchritt } from './hooks/useMassenverarbeitungSchritt';
import type { PathType } from './types';

const fehlerFilters = { ...defaultFilters, status: 'FEHLER' };
const fehlerPagination = { page: 1, pageSize: 100_000 };

export const AusgesteuerteVorgaengeMassBearbeitung: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useVorgaenge(
    fehlerFilters,
    fehlerPagination,
  );

  const vorgaenge = useMemo(() => data?.data ?? [], [data?.data]);

  // Aktuell gewählter Abarbeitungsschritt – filtert die Vorgänge in der Tabelle.
  // In localStorage persistiert, damit er beim Zurückkehren aus einem Vorgang
  // erhalten bleibt.
  const [aktiverSchritt, setAktiverSchritt] = useMassenverarbeitungSchritt();
  // IDs der in der Tabelle ausgewählten Vorgänge, die massenbearbeitet werden sollen
  const [selectedVorgangIds, setSelectedVorgangIds] = useState<Set<string>>(
    () => new Set(),
  );
  // Im JSON-Baum ausgewählte Pfade, deren Werte massenbearbeitet werden sollen
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(
    () => new Set(),
  );
  // Neue Werte pro Pfad aus dem JSON-Baum, die beim Speichern auf alle ausgewählten Vorgänge angewendet werden
  const [patchValues, setPatchValues] = useState<Record<string, string>>({});
  // Explizite Typ-Wahl für manuell hinzugefügte Pfade (für vorhandene Pfade aus dem Baum wird der Typ aus dem Originalwert abgeleitet)
  const [pathTypeOverrides, setPathTypeOverrides] = useState<
    Record<string, PathType>
  >({});
  // Pfade, die beim Speichern aus dem Payload gelöscht werden
  const [pathsToDelete, setPathsToDelete] = useState<Set<string>>(
    () => new Set(),
  );

  const handleSchrittAktivieren = useCallback(
    (schritt: VorgangProzessSchritt | null) => {
      setAktiverSchritt(schritt);
      setSelectedVorgangIds(new Set());
      setSelectedPaths(new Set());
      setPatchValues({});
      setPathTypeOverrides({});
      setPathsToDelete(new Set());
    },
    [],
  );
  const handleSelectedIdsChange = useCallback((next: Set<string>) => {
    setSelectedVorgangIds(next);
    setSelectedPaths(new Set());
    setPatchValues({});
    setPathTypeOverrides({});
    setPathsToDelete(new Set());
  }, []);

  const handleEdit = useCallback(
    (item: VorgangListeItem) => {
      navigate(`/vorgaenge/${item.id}`, {
        state: {
          from: location.pathname,
        } satisfies VorgangBearbeitungLocationState,
      });
    },
    [navigate, location.pathname],
  );

  const handleTogglePath = useCallback((path: string) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleSetValue = useCallback((path: string, value: string) => {
    setPatchValues((prev) => ({ ...prev, [path]: value }));
  }, []);

  const handleAddPath = useCallback((path: string, type: PathType) => {
    setSelectedPaths((prev) => {
      if (prev.has(path)) return prev;
      const next = new Set(prev);
      next.add(path);
      return next;
    });
    setPathTypeOverrides((prev) => ({ ...prev, [path]: type }));
    setPatchValues((prev) => (path in prev ? prev : { ...prev, [path]: '' }));
  }, []);

  const handleToggleDelete = useCallback((path: string) => {
    setPathsToDelete((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleSaved = useCallback(
    (savedVorgangIds: string[], options?: { keepSelection?: boolean }) => {
      if (savedVorgangIds.length === 0) return;
      if (!options?.keepSelection) {
        setSelectedVorgangIds((prev) => {
          const next = new Set(prev);
          for (const id of savedVorgangIds) next.delete(id);
          return next;
        });
      }
      setSelectedPaths(new Set());
      setPatchValues({});
      setPathTypeOverrides({});
      setPathsToDelete(new Set());
    },
    [],
  );

  const selectedVorgangIdList = useMemo(
    () => Array.from(selectedVorgangIds),
    [selectedVorgangIds],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">
          Massenverarbeitung
        </h1>
      </div>

      {error ? (
        <div className="text-red-500">
          Fehler beim Laden der Vorgänge im Status „Fehler".
        </div>
      ) : isLoading ? (
        <div className="flex flex-wrap gap-3">
          <Skeleton className="h-14 w-48" />
        </div>
      ) : (
        <>
          <AbarbeitungsSchrittAuswahl
            vorgaenge={vorgaenge}
            aktiverSchritt={aktiverSchritt}
            onSchrittAktivieren={handleSchrittAktivieren}
          />

          {aktiverSchritt ? (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">
                Vorgänge im Schritt „
                {VORGANG_PROZESS_SCHRITT_LABEL[aktiverSchritt]}"
              </h2>
              <VorgangTable
                key={aktiverSchritt}
                forcedFilters={{
                  status: 'FEHLER',
                  currentStep: aktiverSchritt,
                }}
                showBulkActions={false}
                selectedIds={selectedVorgangIds}
                onSelectedIdsChange={handleSelectedIdsChange}
                onEdit={handleEdit}
              />
            </section>
          ) : null}

          {aktiverSchritt && selectedVorgangIdList.length > 0 ? (
            <PfadWertBearbeitung
              vorgangIds={selectedVorgangIdList}
              schritt={aktiverSchritt}
              selectedPaths={selectedPaths}
              patchValues={patchValues}
              pathTypeOverrides={pathTypeOverrides}
              pathsToDelete={pathsToDelete}
              onTogglePath={handleTogglePath}
              onSetValue={handleSetValue}
              onAddPath={handleAddPath}
              onToggleDelete={handleToggleDelete}
              onSaved={handleSaved}
            />
          ) : null}
        </>
      )}
    </div>
  );
};
